import { MovementChartData, MovementType } from '@/domain/accounts/account'
import { convertToDigitStr } from '@/utils/helpers'
import { useState } from 'react'
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const PALETTE = [
  '#2563eb', // blue-600
  '#16a34a', // green-600
  '#dc2626', // red-600
  '#d97706', // amber-600
  '#7c3aed', // violet-600
  '#0891b2', // cyan-600
  '#db2777', // pink-600
  '#65a30d', // lime-600
]

const TOTAL_KEY = '_total'
const TOTAL_COLOR = '#0f172a' // slate-900

export type ChartCalculationMode = 'cumulative_flow' | 'absolute_balance'

export interface AccountSeries {
  accountId: string
  label: string
  data: MovementChartData[]
}

interface MultiAccountChartProps {
  series: AccountSeries[]
  showTotalToggle?: boolean
}

interface UnifiedPoint {
  date: string
  timestamp: number
  [accountId: string]: any
}

// Build unified timeline: either cumulative movements (starting at 0) or historical balance
const buildUnifiedData = (series: AccountSeries[], mode: ChartCalculationMode) => {
  if (series.length === 0) return []

  // Extract and sort all points
  const allEvents: {
    timestamp: number
    dateStr: string
    accountId: string
    delta: number
    balanceAfter: number
  }[] = []

  for (const s of series) {
    for (const point of s.data) {
      const d = new Date(point.movementDate)
      const isCredit = point.type === MovementType.Credit || (point.type as any) === 'credit'
      const delta = isCredit ? point.amount : -point.amount

      allEvents.push({
        timestamp: d.getTime(),
        dateStr: d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' }),
        accountId: s.accountId,
        delta,
        balanceAfter: point.balanceAfter,
      })
    }
  }

  allEvents.sort((a, b) => a.timestamp - b.timestamp)

  // Track state per account
  // In cumulative mode: each account starts at 0 and adds delta
  // In absolute mode: uses point.balanceAfter
  const runningValues: Record<string, number> = {}
  if (mode === 'cumulative_flow') {
    series.forEach((s) => {
      runningValues[s.accountId] = 0
    })
  }

  const dateMap = new Map<string, UnifiedPoint>()

  for (const ev of allEvents) {
    if (mode === 'cumulative_flow') {
      runningValues[ev.accountId] = (runningValues[ev.accountId] || 0) + ev.delta
    } else {
      runningValues[ev.accountId] = ev.balanceAfter
    }

    let total = 0
    let hasValues = false
    const pointValues: Record<string, number> = {}

    for (const s of series) {
      if (runningValues[s.accountId] !== undefined) {
        pointValues[s.accountId] = runningValues[s.accountId]
        total += runningValues[s.accountId]
        hasValues = true
      }
    }

    dateMap.set(ev.dateStr, {
      date: ev.dateStr,
      timestamp: ev.timestamp,
      ...pointValues,
      [TOTAL_KEY]: hasValues ? total : undefined,
    })
  }

  return Array.from(dateMap.values())
}

const CustomTooltip = ({
  active,
  payload,
  label,
  series,
  mode,
}: {
  active?: boolean
  payload?: Array<{ dataKey: string; value: number; color: string }>
  label?: string
  series: AccountSeries[]
  mode: ChartCalculationMode
}) => {
  if (!active || !payload?.length) return null

  const totalItem = payload.find((p) => p.dataKey === TOTAL_KEY)
  const accountItems = payload.filter((p) => p.dataKey !== TOTAL_KEY)

  const isFlow = mode === 'cumulative_flow'

  return (
    <div className="rounded-lg border bg-background p-3 shadow-md min-w-[220px] space-y-2">
      <div className="flex items-center justify-between border-b pb-1">
        <p className="text-xs font-semibold text-muted-foreground">{label}</p>
        <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider">
          {isFlow ? 'Flujo del Período' : 'Saldo Total'}
        </span>
      </div>

      {totalItem && (
        <div className="flex items-center justify-between gap-4 py-1.5 px-2 rounded bg-muted/60 border font-medium text-xs">
          <div className="flex items-center gap-1.5">
            <span
              className="inline-block w-2.5 h-2.5 rounded-full"
              style={{ background: totalItem.color }}
            />
            <span className="font-semibold text-foreground">
              {isFlow ? 'Flujo Neto Combinado:' : 'Saldo Total Consolidado:'}
            </span>
          </div>
          <span
            className={`font-mono font-bold ${
              totalItem.value >= 0 ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {totalItem.value >= 0 ? '+' : ''}
            {convertToDigitStr(totalItem.value)}
          </span>
        </div>
      )}

      <div className="space-y-1">
        {accountItems.map((entry) => {
          const s = series.find((s) => s.accountId === entry.dataKey)
          return (
            <div key={entry.dataKey} className="flex items-center justify-between gap-4 text-xs">
              <div className="flex items-center gap-1.5">
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{ background: entry.color }}
                />
                <span className="text-muted-foreground truncate max-w-[130px]">
                  {s?.label ?? entry.dataKey}
                </span>
              </div>
              <span
                className={`font-mono font-medium ${
                  entry.value >= 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {entry.value >= 0 ? '+' : ''}
                {convertToDigitStr(entry.value)}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export const MultiAccountChart = ({ series, showTotalToggle = true }: MultiAccountChartProps) => {
  const [mode, setMode] = useState<ChartCalculationMode>('cumulative_flow')
  const [showTotal, setShowTotal] = useState(true)

  const data = buildUnifiedData(series, mode)

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <p className="text-sm">Sin movimientos en el período seleccionado</p>
      </div>
    )
  }

  const isFlow = mode === 'cumulative_flow'

  return (
    <div className="space-y-4">
      {/* Chart controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-muted/30 p-2.5 rounded-lg border">
        <div className="flex items-center gap-2">
          <Label className="text-xs font-semibold text-muted-foreground uppercase">
            Métrica del Gráfico:
          </Label>
          <Select value={mode} onValueChange={(val) => setMode(val as ChartCalculationMode)}>
            <SelectTrigger className="h-8 text-xs w-[240px] bg-background">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cumulative_flow">
                Flujo Acumulado en el Período (Base $0)
              </SelectItem>
              <SelectItem value="absolute_balance">
                Saldo Histórico Absoluto
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {showTotalToggle && series.length > 1 && (
          <div className="flex items-center gap-2">
            <Switch
              id="toggle-total"
              checked={showTotal}
              onCheckedChange={setShowTotal}
            />
            <Label htmlFor="toggle-total" className="text-xs cursor-pointer font-medium text-foreground">
              {isFlow ? 'Línea de Flujo Neto Total' : 'Línea de Saldo Consolidado'}
            </Label>
          </div>
        )}
      </div>

      <div className="h-[380px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 16, left: 10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
              tickFormatter={(v: number) => convertToDigitStr(v)}
              width={100}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip series={series} mode={mode} />} />
            <Legend
              formatter={(value) => {
                if (value === TOTAL_KEY) {
                  return isFlow ? 'Flujo Neto Combinado' : 'Saldo Consolidado (Total)'
                }
                const s = series.find((s) => s.accountId === value)
                return s?.label ?? value
              }}
            />

            {/* Individual Account Lines */}
            {series.map((s, i) => (
              <Line
                key={s.accountId}
                type="monotone"
                dataKey={s.accountId}
                stroke={PALETTE[i % PALETTE.length]}
                strokeWidth={1.75}
                dot={false}
                activeDot={{ r: 4 }}
                connectNulls
              />
            ))}

            {/* Combined/Total Line */}
            {showTotal && series.length > 1 && (
              <Line
                key={TOTAL_KEY}
                type="monotone"
                dataKey={TOTAL_KEY}
                stroke={TOTAL_COLOR}
                strokeWidth={3}
                strokeDasharray="4 2"
                dot={false}
                activeDot={{ r: 5, stroke: TOTAL_COLOR, strokeWidth: 2 }}
                connectNulls
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
